import { ICellRendererAngularComp } from '@ag-grid-community/angular';
import { ICellRendererParams } from '@ag-grid-community/core';
import { NgClass } from '@angular/common';
import { Component } from '@angular/core';

import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { MultiTooltipDirective } from '@shared/directives/multi-tooltip.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';

export type ICheckboxCellRendererParams<T = unknown> = ICellRendererParams<T> & {
  checkboxContainerClass: string;
  checkboxTooltip: (() => string[]) | ((params: ICheckboxCellRendererParams<T>) => string[]);
  tooltipField?: string;
};

// todo move this component to shared folder
@Component({
  imports: [TooltipDirective, MultiTooltipDirective, NgClass, CheckboxComponent],
  styles: [
    `
      :host {
        @apply block h-full w-full;
      }
    `,
  ],
  template: `
    <div class="flex h-full items-center">
      @if (!pinned) {
        <div
          class="flex h-full border-r border-[var(--ag-border-color)]"
          [ngClass]="params.checkboxContainerClass"
        >
          <aux-checkbox
            [disabled]="!selectable"
            [auxMultiTooltip]="checkboxTooltip"
            [checked]="!!isSelected"
            [indeterminate]="isSelected === undefined"
            (customChange)="selectRow()"
            [hideLabel]="true"
          />
        </div>
      }

      <span
        class="ag-cell-value w-full"
        role="presentation"
        [style.paddingLeft.px]="paddingLeft"
        [auxTooltip]="tooltip"
      >
        <span class="ag-cell-wrapper w-full">
          @if (isGroup) {
            @if (params.node.expanded) {
              <span class="ag-group-expanded" (click)="onClick()">
                <span class="ag-icon ag-icon-tree-open" role="presentation"></span>
              </span>
            } @else {
              <span class="ag-group-contracted" (click)="onClick()">
                <span class="ag-icon ag-icon-tree-closed" role="presentation"></span>
              </span>
            }
          }

          <span class="ag-group-value" [ngClass]="{ 'ml-[11px]': !isGroup }">
            {{ params.value }}
          </span>
        </span>
      </span>
    </div>
  `,
})
export class AgBeCheckboxGroupRendererComponent implements ICellRendererAngularComp {
  checkboxTooltip: string[] = [];
  isGroup!: boolean;
  isSelected: boolean | undefined = false;
  paddingLeft!: number;
  params!: ICheckboxCellRendererParams<Record<string, unknown>>;
  pinned = false;
  selectable = false;
  tooltip = '';

  agInit(params: ICheckboxCellRendererParams<Record<string, unknown>>): void {
    this.params = params;
    this.paddingLeft = params.node.level * 10;
    this.isGroup = !!params.node.group;
    this.pinned = params.node.isRowPinned();
    const tooltipDefault = this.isGroup ? params.value : '';

    this.tooltip =
      params.data && params.tooltipField ? params.data[params.tooltipField] : tooltipDefault;
    this.isSelected = params.node.isSelected();
    this.selectable = params.node.selectable;
    this.updateCheckboxTooltip();

    this.params.node.addEventListener('selectableChanged', this.onSelectableChanged);
    this.params.node.addEventListener('rowSelected', this.onRowSelected);
  }

  destroy() {
    this.params.node.removeEventListener('rowSelected', this.onRowSelected);
    this.params.node.removeEventListener('selectableChanged', this.onSelectableChanged);
  }

  onClick() {
    this.params.node.setExpanded(!this.params.node.expanded);
  }

  onRowSelected = () => {
    this.isSelected = this.params.node.isSelected();
    this.updateCheckboxTooltip();
  };

  onSelectableChanged = () => {
    this.selectable = this.params.node.selectable;
    this.updateCheckboxTooltip();
  };

  refresh() {
    return false;
  }

  selectRow() {
    if (this.params.node.selectable) {
      this.params.node.setSelected(!this.params.node.isSelected());
      this.isSelected = this.params.node.isSelected();
    }
  }

  updateCheckboxTooltip() {
    this.checkboxTooltip = this.params.checkboxTooltip(this.params);
  }
}
